import { useState, useEffect, useCallback } from 'react';
import axios from '../components/AxiosInstance';
import { tutorialStepsDesktop, tutorialStepsMobile } from '../components/tutorialSteps';

/**
 * Hook personnalisé pour gérer l'état du tutoriel
 * @param {boolean} isMobile - Indique si l'utilisateur est sur mobile
 * @param {function} setActiveView - Fonction pour changer la vue active (mobile uniquement)
 * @returns {Object} État et fonctions du tutoriel
 */
const useTutorial = (isMobile = false, setActiveView = null) => {
    const [tutorialActive, setTutorialActive] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [tutorialCompleted, setTutorialCompleted] = useState(false);
    const [loading, setLoading] = useState(true);

    // Sélectionner les étapes en fonction du type d'appareil
    const steps = isMobile ? tutorialStepsMobile : tutorialStepsDesktop;

    // Récupérer le statut du tutoriel au chargement
    useEffect(() => {
        const fetchTutorialStatus = async () => {
            try {
                const response = await axios.get('/api/tutorial/status');
                const completed = response.data.tutorial_completed;
                setTutorialCompleted(completed);
                console.log(completed);

                // Activer le tutoriel si non complété
                if (!completed) {
                    setTutorialActive(true);
                }
            } catch (error) {
                console.error('Erreur lors de la récupération du statut du tutoriel:', error);
                // En cas d'erreur, ne pas afficher le tutoriel
                setTutorialCompleted(true);
            } finally {
                setLoading(false);
            }
        };

        fetchTutorialStatus();
    }, []);

    // Passer à l'étape suivante
    const nextStep = useCallback(() => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            completeTutorial();
        }
    }, [currentStep, steps.length]);

    // Effet pour forcer le changement de vue sur mobile
    useEffect(() => {
        if (tutorialActive && isMobile && setActiveView && steps[currentStep]?.forceView) {
            setActiveView(steps[currentStep].forceView);
        }
    }, [currentStep, isMobile, setActiveView, steps, tutorialActive]);

    // Revenir à l'étape précédente
    const previousStep = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    }, [currentStep]);

    // Passer le tutoriel
    const skipTutorial = useCallback(() => {
        setTutorialCompleted(true);
        setTutorialActive(false);
    }, []);

    // Compléter le tutoriel
    const completeTutorial = useCallback(() => {
        setTutorialCompleted(true);
        setTutorialActive(false);
    }, []);

    // Réinitialiser le tutoriel (pour debug/test)
    const resetTutorial = useCallback(() => {
        setCurrentStep(0);
        setTutorialActive(true);
        setTutorialCompleted(false);
    }, []);

    return {
        tutorialActive,
        currentStep,
        tutorialCompleted,
        loading,
        steps,
        nextStep,
        previousStep,
        skipTutorial,
        completeTutorial,
        resetTutorial
    };
};

export default useTutorial;
